<script lang="ts">
	// Hover card for a sidebar chat row (SAY-1806, after Open WebUI's
	// LinkPreview): rest the pointer on a chat for 300ms and a card opens to the
	// right with the title, the message count and the end of the conversation
	// (user bubbles right, assistant text left, each with its time). Desktop
	// only; the row's drag / selection modes disable it. The transcript is
	// fetched on first open and cached per chat until its last activity moves.
	import { LinkPreview } from 'bits-ui';
	import type { Snippet } from 'svelte';
	import { loadHistory } from '$lib/api';
	import { foldHistory } from '$lib/history-fold';
	import { transcriptRows, type TranscriptRow } from '$lib/chat-search';
	import { formatPreviewTime, messageCountLabel, previewTail } from '$lib/chat-preview';

	let {
		sessionId,
		title,
		lastActivityAt,
		disabled = false,
		class: className = '',
		children,
		...rest
	}: LinkPreview.TriggerProps & {
		sessionId: string;
		title: string;
		lastActivityAt: number;
		disabled?: boolean;
		class?: string;
		children: Snippet;
	} = $props();

	let open = $state(false);
	let triggerEl = $state<HTMLElement | null>(null);
	// The card opens flush with the sidebar's right edge (+8px), not with the end
	// of the link text, which stops short of the time-ago column.
	let sideOffset = $state(8);
	let rows = $state<TranscriptRow[] | null>(null);
	let failed = $state(false);

	const tail = $derived(rows ? previewTail(rows) : []);

	async function load(): Promise<void> {
		const hit = cache.get(sessionId);
		if (hit && hit.at === lastActivityAt) {
			rows = hit.rows;
			return;
		}
		try {
			const entries = await loadHistory(sessionId);
			const next = transcriptRows(foldHistory(entries).messages);
			cache.set(sessionId, { at: lastActivityAt, rows: next });
			rows = next;
			failed = false;
		} catch {
			failed = true;
		}
	}

	$effect(() => {
		if (open && !disabled) {
			const sidebar = triggerEl?.closest('[data-slot="sidebar-container"]')?.getBoundingClientRect();
			const trigger = triggerEl?.getBoundingClientRect();
			sideOffset = sidebar && trigger ? Math.max(8, Math.round(sidebar.right - trigger.right) + 8) : 8;
			void load();
		}
	});
	$effect(() => {
		if (disabled) open = false;
	});
</script>

<script module lang="ts">
	// One transcript per chat, keyed by its sidebar last-activity stamp so a
	// chat that moved on is refetched on the next hover.
	const cache = new Map<string, { at: number; rows: TranscriptRow[] }>();
</script>

<LinkPreview.Root bind:open openDelay={300} closeDelay={0}>
	<LinkPreview.Trigger bind:ref={triggerEl} class={className} {...rest}>
		{@render children()}
	</LinkPreview.Trigger>
	{#if !disabled}
		<LinkPreview.Portal>
			<LinkPreview.Content
				side="right"
				{sideOffset}
				align="start"
				class="z-[9999] hidden max-h-[min(17.5rem,calc(100vh-1.5rem))] w-[20rem] max-w-[calc(100vw-1.5rem)] flex-col overflow-hidden rounded-2xl bg-white text-[0.8125rem] leading-5 text-gray-700 shadow-[0_16px_40px_-28px_rgba(0,0,0,0.55)] ring-1 ring-black/5 outline-hidden md:flex dark:bg-gray-850 dark:text-gray-300 dark:ring-white/10"
				data-testid="chat-hover-preview"
			>
				<div class="flex shrink-0 items-center gap-2 border-b border-gray-50/60 px-3 py-2 dark:border-gray-800/25">
					<span class="min-w-0 flex-1 truncate font-normal text-gray-900 dark:text-gray-100">{title}</span>
					{#if rows}
						<span class="shrink-0 text-xs text-gray-500 dark:text-gray-400">{messageCountLabel(rows.length)}</span>
					{/if}
				</div>
				<div class="scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-3 py-2">
					{#if failed}
						<p class="py-3 text-xs text-gray-500 dark:text-gray-400">Couldn’t load this chat.</p>
					{:else if !rows}
						<p class="py-3 text-xs text-gray-400 dark:text-gray-500">Loading…</p>
					{:else if tail.length === 0}
						<p class="py-3 text-xs text-gray-500 dark:text-gray-400">Nothing has been said in this chat yet</p>
					{:else}
						<ol class="flex flex-col gap-2">
							{#each tail as row, i (i)}
								{#if row.role === 'user'}
									<li class="flex flex-col items-end gap-0.5">
										<span class="pr-1 text-[0.6875rem] text-gray-400 dark:text-gray-500">{formatPreviewTime(row.timestamp)}</span>
										<div class="max-w-[90%] rounded-2xl bg-gray-50 px-3 py-1.5 break-words whitespace-pre-wrap text-gray-900 dark:bg-white/[0.09] dark:text-gray-100">
											{row.text}
										</div>
									</li>
								{:else}
									<li class="flex flex-col items-start gap-0.5">
										<div class="px-1 break-words whitespace-pre-wrap text-gray-800 dark:text-gray-200">{row.text}</div>
										<span class="pl-1 text-[0.6875rem] text-gray-400 dark:text-gray-500">{formatPreviewTime(row.timestamp)}</span>
									</li>
								{/if}
							{/each}
						</ol>
					{/if}
				</div>
			</LinkPreview.Content>
		</LinkPreview.Portal>
	{/if}
</LinkPreview.Root>
